import { create } from 'zustand';
import { DungeonEntity } from '../entities/DungeonEntity';
import {
  DevtoolPopupPosition,
  DevtoolPopupSize,
  DevtoolState,
} from '../types/store.types';

export type { DevtoolState };

const LAYOUT_STORAGE_KEY = 'usagi.devtool.layout';
const TOGGLES_STORAGE_KEY = 'usagi.devtool.toggles';

interface PersistedToggles {
  devModeEnabled: boolean;
  showChessboard: boolean;
  showCoordinates: boolean;
  showTileIndex: boolean;
}

const DEFAULT_TOGGLES: PersistedToggles = {
  devModeEnabled: false,
  showChessboard: false,
  showCoordinates: false,
  showTileIndex: false,
};

function loadToggles(): PersistedToggles {
  try {
    const raw = localStorage.getItem(TOGGLES_STORAGE_KEY);
    if (!raw) {
      return { ...DEFAULT_TOGGLES };
    }
    const parsed = JSON.parse(raw) as Partial<PersistedToggles>;
    return {
      devModeEnabled: parsed.devModeEnabled ?? DEFAULT_TOGGLES.devModeEnabled,
      showChessboard: parsed.showChessboard ?? DEFAULT_TOGGLES.showChessboard,
      showCoordinates:
        parsed.showCoordinates ?? DEFAULT_TOGGLES.showCoordinates,
      showTileIndex: parsed.showTileIndex ?? DEFAULT_TOGGLES.showTileIndex,
    };
  } catch {
    return { ...DEFAULT_TOGGLES };
  }
}

function persistToggles(toggles: PersistedToggles): void {
  try {
    localStorage.setItem(TOGGLES_STORAGE_KEY, JSON.stringify(toggles));
  } catch {
    /* ignore */
  }
}

const DEFAULT_DEVTOOL_POSITION: DevtoolPopupPosition = { x: 10, y: 10 };
const DEFAULT_CONSOLE_POSITION: DevtoolPopupPosition = { x: 10, y: 420 };
const DEFAULT_CONSOLE_SIZE: DevtoolPopupSize = { width: 600, height: 300 };

interface PersistedLayout {
  devtoolPosition: DevtoolPopupPosition;
  consolePosition: DevtoolPopupPosition;
  consoleSize: DevtoolPopupSize;
}

function loadLayout(): PersistedLayout {
  try {
    const raw = localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (!raw) {
      return {
        devtoolPosition: { ...DEFAULT_DEVTOOL_POSITION },
        consolePosition: { ...DEFAULT_CONSOLE_POSITION },
        consoleSize: { ...DEFAULT_CONSOLE_SIZE },
      };
    }
    const parsed = JSON.parse(raw) as Partial<PersistedLayout>;
    return {
      devtoolPosition: parsed.devtoolPosition ?? {
        ...DEFAULT_DEVTOOL_POSITION,
      },
      consolePosition: parsed.consolePosition ?? {
        ...DEFAULT_CONSOLE_POSITION,
      },
      consoleSize: parsed.consoleSize ?? { ...DEFAULT_CONSOLE_SIZE },
    };
  } catch {
    return {
      devtoolPosition: { ...DEFAULT_DEVTOOL_POSITION },
      consolePosition: { ...DEFAULT_CONSOLE_POSITION },
      consoleSize: { ...DEFAULT_CONSOLE_SIZE },
    };
  }
}

function persistLayout(layout: PersistedLayout): void {
  try {
    localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layout));
  } catch {
    /* ignore */
  }
}

interface DevtoolActions {
  setShowTileIndex: (v: boolean) => void;
  setDevModeEnabled: (v: boolean) => void;
  setShowChessboard: (v: boolean) => void;
  setShowCoordinates: (v: boolean) => void;
  setEntities: (e: DungeonEntity[]) => void;
  setEntityTick: (t: number) => void;
  toggleDevtoolPanelVisible: () => void;
  toggleConsoleVisible: () => void;
  setGalleryOpen: (v: boolean) => void;
  toggleGalleryOpen: () => void;
  setDevtoolPosition: (p: DevtoolPopupPosition) => void;
  setConsolePosition: (p: DevtoolPopupPosition) => void;
  setConsoleSize: (s: DevtoolPopupSize) => void;
  resetPopupLayouts: () => void;
}

const initialLayout = loadLayout();
const initialToggles = loadToggles();

export const useDevtoolStore = create<DevtoolState & DevtoolActions>(
  (set, get) => ({
    showTileIndex: initialToggles.showTileIndex,
    devModeEnabled: initialToggles.devModeEnabled,
    showChessboard: initialToggles.showChessboard,
    showCoordinates: initialToggles.showCoordinates,
    entities: [],
    entityTick: 0,
    devtoolPanelVisible: false,
    consoleVisible: false,
    galleryOpen: false,
    devtoolPosition: initialLayout.devtoolPosition,
    consolePosition: initialLayout.consolePosition,
    consoleSize: initialLayout.consoleSize,

    setShowTileIndex: (v) => {
      set({ showTileIndex: v });
      const s = get();
      persistToggles({
        devModeEnabled: s.devModeEnabled,
        showChessboard: s.showChessboard,
        showCoordinates: s.showCoordinates,
        showTileIndex: v,
      });
    },
    setDevModeEnabled: (v) => {
      set({ devModeEnabled: v });
      const s = get();
      persistToggles({
        devModeEnabled: v,
        showChessboard: s.showChessboard,
        showCoordinates: s.showCoordinates,
        showTileIndex: s.showTileIndex,
      });
    },
    setShowChessboard: (v) => {
      set({ showChessboard: v });
      const s = get();
      persistToggles({
        devModeEnabled: s.devModeEnabled,
        showChessboard: v,
        showCoordinates: s.showCoordinates,
        showTileIndex: s.showTileIndex,
      });
    },
    setShowCoordinates: (v) => {
      set({ showCoordinates: v });
      const s = get();
      persistToggles({
        devModeEnabled: s.devModeEnabled,
        showChessboard: s.showChessboard,
        showCoordinates: v,
        showTileIndex: s.showTileIndex,
      });
    },
    setEntities: (e) => set({ entities: e }),
    setEntityTick: (t) => set({ entityTick: t }),

    toggleDevtoolPanelVisible: () =>
      set((s) => ({ devtoolPanelVisible: !s.devtoolPanelVisible })),
    toggleConsoleVisible: () =>
      set((s) => ({ consoleVisible: !s.consoleVisible })),

    setGalleryOpen: (v) => set({ galleryOpen: v }),
    toggleGalleryOpen: () => set((s) => ({ galleryOpen: !s.galleryOpen })),

    setDevtoolPosition: (p) => {
      set({ devtoolPosition: p });
      const s = get();
      persistLayout({
        devtoolPosition: p,
        consolePosition: s.consolePosition,
        consoleSize: s.consoleSize,
      });
    },

    setConsolePosition: (p) => {
      set({ consolePosition: p });
      const s = get();
      persistLayout({
        devtoolPosition: s.devtoolPosition,
        consolePosition: p,
        consoleSize: s.consoleSize,
      });
    },

    setConsoleSize: (sz) => {
      set({ consoleSize: sz });
      const s = get();
      persistLayout({
        devtoolPosition: s.devtoolPosition,
        consolePosition: s.consolePosition,
        consoleSize: sz,
      });
    },

    resetPopupLayouts: () => {
      const layout: PersistedLayout = {
        devtoolPosition: { ...DEFAULT_DEVTOOL_POSITION },
        consolePosition: { ...DEFAULT_CONSOLE_POSITION },
        consoleSize: { ...DEFAULT_CONSOLE_SIZE },
      };
      set(layout);
      persistLayout(layout);
    },
  }),
);
